import { useCallback, useEffect, useRef, useState } from 'react';
import { apiService } from '../../../services/api';

/** Order-insensitive identity for a dataset selection. */
const datasetKey = (ids) => [...(ids || [])].map(String).sort().join('|');

/**
 * Conversational alert authoring, one turn at a time (PRD-1381).
 *
 * **The console owns the conversation.** Nothing is stored server-side — the
 * agent's graph compiles with no checkpointer, which is what makes PRD-1378's
 * "no persistence" structural rather than a promise — so the transcript and the
 * slots live here and are replayed on every turn. Losing this state loses the
 * conversation, which is also why the wizard keeps it across step changes.
 *
 * **Falling back to the form is a decision, not a fallthrough.** Replacing the
 * wizard made authoring depend on a model being reachable, and the form always
 * worked; an outage must not mean nobody can create an alert. `agentUnavailable`
 * latches once set, so a flaky agent cannot bounce an author between two
 * different step 2s mid-sentence.
 *
 * What does NOT trip it: a 429. That is the far side's concurrency bound — four
 * builds already running — and retrying works. Dropping someone into the form
 * because the system was briefly busy would be the wrong answer to a fixable
 * wait.
 */
export function useAlertConversation({ datasetIds = [], anchorDate = null } = {}) {
  const [messages, setMessages] = useState([]);
  const [slots, setSlots] = useState({});
  const [settled, setSettled] = useState(false);
  const [definition, setDefinition] = useState(null);
  const [sentence, setSentence] = useState('');
  // The agent's proposed name for the alert it built (PRD-1447). Copied out of
  // the reply like every field beside it, which is the point: this hook picks
  // named fields rather than keeping the reply, so a field it does not name is
  // dropped here with nothing reporting it — the fifth place on the way from the
  // agent where that is true.
  const [suggestedName, setSuggestedName] = useState('');
  const [refusalReason, setRefusalReason] = useState(null);
  // Clickable answers for the newest question. Empty when the choice is not
  // closed — free text always works, so an empty list is not a dead end.
  const [options, setOptions] = useState([]);
  const [v2Candidate, setV2Candidate] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);
  const [retryAfter, setRetryAfter] = useState(null);
  const [agentUnavailable, setAgentUnavailable] = useState(false);
  // The eight-turn cap, reported by the agent as `action: "capped"`. The server
  // will not advance the conversation again, so the composer has to stop
  // inviting input rather than silently swallowing it.
  const [capped, setCapped] = useState(false);
  // Set when the author changed the selected datasets after a definition was
  // built. Not a warning — the built alert reads data that is no longer
  // selected, so it cannot be saved as-is.
  const [staleDefinition, setStaleDefinition] = useState(false);
  // Set when they changed datasets mid-conversation with nothing built yet.
  const [datasetsChangedMidConversation, setDatasetsChangedMidConversation] = useState(false);

  // One in flight at a time. A second send while the first is running would
  // replay a transcript that is about to change, and the later answer would
  // overwrite the earlier one with slots derived from a shorter conversation.
  const inFlight = useRef(null);

  // The dataset selection this conversation has been talking about. Compared by
  // value: `datasetIds` is a prop array whose identity changes every render.
  const datasetKeyRef = useRef(datasetKey(datasetIds));

  useEffect(() => {
    const next = datasetKey(datasetIds);
    if (next === datasetKeyRef.current) return;
    datasetKeyRef.current = next;

    // **After a Build, invalidate rather than warn.** The definition was
    // generated against a scope that no longer holds, so letting Next stay
    // enabled would ship an alert built on data the author has since removed.
    // Deliberately conservative: removing an unrelated dataset invalidates too.
    // Being precise would mean mapping the IR's tables back to datasets, and the
    // console has no such mapping — resolve_permitted_tables does it server-side
    // and the response does not say which tables it used. Guessing there would
    // be worse than rebuilding.
    if (definition) {
      setDefinition(null);
      setSuggestedName('');
      setSettled(false);
      setStaleDefinition(true);
      // The stronger signal wins: showing "rebuild it" and "your answers may be
      // stale" together would be two messages about one action.
      return;
    }

    // **Before a Build, warn rather than reset.** Slots are plain text —
    // "unscheduled absences", not column names — so nothing here can tell which
    // answer referenced which table. Resetting would throw away a long
    // conversation on a guess, and an author who loses one that way stops
    // touching the dataset tags at all.
    setDatasetsChangedMidConversation(messages.length > 0);
  }, [datasetIds, definition, messages.length]);

  const reset = useCallback(() => {
    setMessages([]);
    setSlots({});
    setSettled(false);
    setDefinition(null);
    setSentence('');
    setSuggestedName('');
    setRefusalReason(null);
    setOptions([]);
    setV2Candidate(false);
    setError(null);
    setRetryAfter(null);
    setStaleDefinition(false);
    setDatasetsChangedMidConversation(false);
    // **The cap is about this conversation, so a new one is not capped**
    // (PRD-1447). Left set, "Start again" emptied the thread and kept the
    // composer disabled — the same dead end with the transcript gone. Nothing
    // called reset before then, so nothing showed it. `agentUnavailable` is
    // deliberately NOT cleared: it latches so a flaky agent cannot bounce the
    // author between two step 2s, and a restart is not evidence it recovered.
    setCapped(false);
  }, []);

  const send = useCallback(
    async (text) => {
      const trimmed = String(text || '').trim();
      if (!trimmed || pending || agentUnavailable) return;

      // Appended before the call so the author sees their own words immediately
      // — a 29s median with nothing on screen reads as a dropped click.
      const nextMessages = [...messages, { role: 'user', content: trimmed }];
      setMessages(nextMessages);
      setPending(true);
      setError(null);
      setRetryAfter(null);
      // Both notices are about a change the author has now acted on by
      // continuing, so they clear on the next turn rather than lingering.
      setStaleDefinition(false);
      setDatasetsChangedMidConversation(false);

      const controller = new AbortController();
      inFlight.current = controller;

      try {
        const reply = await apiService.alertConversationTurn(
          { messages: nextMessages, slots, datasetIds, anchorDate },
          { signal: controller.signal },
        );

        setMessages([
          ...nextMessages,
          // Options ride on the message so the thread can render them beside
          // the question they belong to, and an older question keeps the ones
          // it was asked with rather than borrowing the newest.
          { role: 'assistant', content: reply.reply || '', options: reply.options || [] },
        ]);
        // Replaced wholesale, never merged. The agent returns the full slot set
        // each turn, and a correction works by REPLACING a slot — merging would
        // keep the answer the author just changed and produce a sentence that
        // contradicts itself.
        setSlots(reply.slots || {});
        setSettled(Boolean(reply.settled));
        setDefinition(reply.definition || null);
        setSentence(reply.sentence || '');
        setSuggestedName(reply.suggestedName || '');
        setRefusalReason(reply.refusalReason || null);
        setOptions(reply.options || []);
        setV2Candidate(Boolean(reply.v2Candidate));

        // The agent returns a discriminated `action` on every reply, and two of
        // its values mean something no other field carries. Deriving state from
        // `settled` and `definition` alone left both invisible.
        //
        // `error` is an outage the far side caught and answered 200 to. Without
        // this it renders as an ordinary assistant message, the composer stays
        // live and the fallback never appears — so the SAME outage behaves one
        // way when the transport notices it and another when the agent does. It
        // latches here for the reason it latches there.
        if (reply.action === 'error') setAgentUnavailable(true);
        // `capped` is the turn limit. The server will not advance this
        // conversation again, so leaving the composer live invites the author to
        // keep typing into something that has already stopped.
        setCapped(reply.action === 'capped');
      } catch (err) {
        if (err?.name === 'AbortError') return;
        setError(err);
        setRetryAfter(err?.retryAfter ?? null);
        if (err?.agentUnavailable) setAgentUnavailable(true);
        // The author's own message stays on screen. Removing it would lose what
        // they typed, which is theirs and not ours to discard on our failure.
      } finally {
        setPending(false);
        inFlight.current = null;
      }
    },
    [messages, slots, datasetIds, anchorDate, pending, agentUnavailable],
  );

  const cancel = useCallback(() => {
    inFlight.current?.abort();
    setPending(false);
  }, []);

  return {
    messages,
    slots,
    settled,
    definition,
    sentence,
    suggestedName,
    refusalReason,
    options,
    v2Candidate,
    pending,
    error,
    retryAfter,
    agentUnavailable,
    capped,
    staleDefinition,
    datasetsChangedMidConversation,
    send,
    cancel,
    reset,
    // True once the author has said anything. The thread and its hairline stay
    // hidden until then, so at rest the step reads as a single textarea rather
    // than announcing itself as a chat.
    started: messages.length > 0,
  };
}
